import { useCallback, useEffect, useState } from "react";
import { TextInput, type TextInputProps } from "~/components/form/TextInput";

import { Space } from "~/constants/sizing";
import { LineHeights } from "~/constants/typography";
import { useTheme } from "~/providers/ThemeProvider";

type ManuallyControlledLines = {
    numberOfLines: number;
    readonly minLines?: never;
    readonly maxLines?: never;
};

type AutomaticallyControlledLines = {
    numberOfLines?: never;
    readonly minLines?: number;
    readonly maxLines?: number;
};

export type TextAreaProps = {
    multiline?: true;
    readonly minLines?: number;
    readonly maxLines?: number;
} & (ManuallyControlledLines | AutomaticallyControlledLines) &
    Omit<TextInputProps, "left" | "right">;

export const TextArea = ({
    numberOfLines,
    onChangeText,
    minLines = 3,
    maxLines = 6,
    ...textInputProps
}: TextAreaProps) => {
    const { inputs } = useTheme();
    const [lines, setLines] = useState(numberOfLines ?? minLines);

    // biome-ignore lint/correctness/useExhaustiveDependencies(minLines): Only change if number of lines changes
    useEffect(() => setLines(numberOfLines ?? minLines), [numberOfLines]);

    const handleTextChange = useCallback(
        (content: string) => {
            if (!numberOfLines) {
                const contentLines = content.split("\n").length;

                setLines(Math.max(minLines, Math.min(maxLines, contentLines + 1)));
            }

            onChangeText?.(content);
        },
        [numberOfLines, onChangeText, minLines, maxLines],
    );

    const maxHeight = Space.sm + maxLines * LineHeights.base + Space.sm;

    return (
        <TextInput
            {...textInputProps}
            onChangeText={handleTextChange}
            multiline={true}
            numberOfLines={lines}
            style={[
                inputs.textArea,
                {
                    maxHeight,
                },
            ]}
        />
    );
};
